import { HISTOGRAM_BUCKET, MAX_TOTAL } from './config';

export const HISTOGRAM_BUCKETS = MAX_TOTAL / HISTOGRAM_BUCKET; // 20

/** Bucket index for a total: 0–49 → 0 … 950–1000 → 19. */
export function bucketIndex(total: number): number {
  const t = Math.min(MAX_TOTAL, Math.max(0, Math.floor(total)));
  return Math.min(HISTOGRAM_BUCKETS - 1, Math.floor(t / HISTOGRAM_BUCKET));
}

export function emptyHistogram(): number[] {
  return new Array<number>(HISTOGRAM_BUCKETS).fill(0);
}

export function buildHistogram(totals: Iterable<number>): number[] {
  const h = emptyHistogram();
  for (const t of totals) h[bucketIndex(t)] = (h[bucketIndex(t)] ?? 0) + 1;
  return h;
}

/**
 * "Better than X%": share of players in strictly lower buckets (PRD §8.1).
 * Returns null when there is nobody else to compare against.
 */
export function betterThanPercent(histogram: readonly number[], total: number): number | null {
  const players = histogram.reduce((s, n) => s + n, 0);
  if (players <= 1) return null;
  const idx = bucketIndex(total);
  let lower = 0;
  for (let i = 0; i < idx; i++) lower += histogram[i] ?? 0;
  return Math.round((lower / players) * 100);
}

/** Parse histogram_json defensively; anything malformed becomes an empty histogram. */
export function parseHistogram(json: string | null | undefined): number[] {
  if (!json) return emptyHistogram();
  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed) || parsed.length !== HISTOGRAM_BUCKETS) return emptyHistogram();
    return parsed.map((n) => (typeof n === 'number' && Number.isFinite(n) ? n : 0));
  } catch {
    return emptyHistogram();
  }
}
